import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Store, MapPin, Clock, Info as InfoIcon } from "lucide-react";
import { format, parseISO, isValid, addDays, startOfDay, isBefore, isSameDay } from "date-fns";
import api from "../lib/api";
import Logo from "../components/portal/Logo";
import { Calendar } from "../components/ui/calendar";
import { deriveInvoiceGroup } from "@/lib/invoice";
import { useOtpVerification } from "@/lib/otp";
import OtpVerificationPanel from "@/components/portal/OtpVerificationPanel";

const luxuryError = "mt-2 text-[11px] text-[#8A2A2A]";

export default function PickupForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoice = location.state?.invoice_number || "";
  const groupType = deriveInvoiceGroup(invoice) || "";

  // Guardrail: invoice must come from the dedicated Invoice Number page.
  useEffect(() => {
    if (!groupType) navigate("/invoice", { replace: true });
  }, [groupType, navigate]);

  const [config, setConfig] = useState({ store: null, slots: [], disabled_weekdays: [5, 6] });
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [slot, setSlot] = useState("");
  const [instructions, setInstructions] = useState("");
  const [customisation, setCustomisation] = useState("no"); // preselected: No
  const [customisationDetails, setCustomisationDetails] = useState("");
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otpContextNotice, setOtpContextNotice] = useState("");
  const otp = useOtpVerification();
  const { reset: resetOtp } = otp;
  const hadVerifiedOtp = useRef(false);
  const otpContextKey = JSON.stringify({
    invoice,
    groupType,
    fulfillmentType: "store_pickup",
    mobile,
  });

  const getOtpPayload = () => ({
    fulfillment_type: "store_pickup",
    invoice_number: invoice,
    group_type: groupType,
    customer_name: name.trim(),
    mobile: mobile.trim(),
    pickup_date: pickupDate,
    pickup_time_slot: slot,
    pickup_instructions: instructions.trim(),
    additional_customisation: customisation,
    customisation_details: customisation === "yes" ? customisationDetails.trim() : "",
    terms_accepted: terms,
  });

  useEffect(() => {
    if (hadVerifiedOtp.current) {
      setOtpContextNotice("Mobile number, invoice, or pickup type changed — please verify again.");
    }
    resetOtp();
  }, [otpContextKey, resetOtp]);

  useEffect(() => {
    hadVerifiedOtp.current = otp.canProceed;
  }, [otp.canProceed]);
  const [touched, setTouched] = useState({});
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  useEffect(() => {
    api.get("/pickup/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const mobileValid = /^[6-9]\d{9}$/.test(mobile);
  const today = startOfDay(new Date());

  const pickupDateValid = useMemo(() => {
    if (!pickupDate) return false;
    const d = parseISO(pickupDate);
    if (!isValid(d)) return false;
    if (d < today) return false;
    // JS getDay(): Sun=0, Sat=6. Backend disabled_weekdays uses Python weekday(): Mon=0..Sun=6.
    const jsDay = d.getDay();
    // Sat=6(js) === 5(py) ; Sun=0(js) === 6(py). Treat both as disabled.
    if (jsDay === 0 || jsDay === 6) return false;
    return true;
  }, [pickupDate, today]);

  const errors = {
    name: !name.trim() ? "Please enter your name." : null,
    mobile: !mobileValid ? "Please enter a valid 10-digit Indian mobile number." : null,
    pickupDate: !pickupDate
      ? "Please select a pickup date."
      : !pickupDateValid
      ? "Please choose a weekday (Mon–Fri) that is today or later."
      : null,
    slot: !slot ? "Please select your preferred pickup time." : null,
    customisation:
      !customisation
        ? "Please make a selection."
        : customisation === "yes" && !customisationDetails.trim()
        ? "Please describe your additional paid customisation(s)."
        : null,
    terms: !terms ? "Please confirm to continue." : null,
  };
  const err = (k) => (touched[k] ? errors[k] : null);

  const canSubmit = !Object.values(errors).some(Boolean) && !submitting;

  const submit = async () => {
    setTouched({ invoice: true, name: true, mobile: true, pickupDate: true, slot: true, customisation: true, terms: true });
    if (Object.values(errors).some(Boolean)) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (otp.required && !otp.canProceed) {
      toast.error("Please verify your mobile number before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = getOtpPayload();
      const { data } = await api.post("/submissions", payload);
      const slotLabel = (config.slots.find((s) => s.value === slot) || {}).label || slot;
      navigate("/pickup/success", {
        state: {
          submission_id: data.submission_id,
          invoice_number: data.invoice_number,
          customer_name: name.trim(),
          pickup_date: pickupDate,
          pickup_slot: slotLabel,
          store: config.store,
          whatsapp_scheduled: data.whatsapp_scheduled,
          group_type: data.group_type,
        },
        replace: true,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(msg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-line bg-ivory/70 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-5 sm:px-8 py-5">
          <Logo />
          <button
            data-testid="change-fulfillment-btn"
            onClick={() => navigate("/select")}
            className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.6} /> Change fulfillment option
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-8"
        >
          <p className="overline text-[10px] mb-2">In-Store Pickup</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
            Schedule your store visit
          </h1>
        </motion.div>

        {/* Store info card */}
        {config.store && (
          <div className="bg-white border border-line rounded-2xl p-5 sm:p-6 mb-6" data-testid="store-info-card">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-2xl bg-maroon/[0.06] text-maroon flex items-center justify-center flex-shrink-0">
                <Store size={20} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="overline text-[10px] mb-1">Pickup Location</p>
                <p className="font-serif text-lg text-ink">{config.store.name}</p>
                <p className="text-sm text-muted2 mt-1 leading-relaxed">
                  {config.store.address_line1}<br />
                  {config.store.address_line2}
                </p>
                <a
                  href={config.store.maps_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="store-maps-link"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-maroon hover:underline"
                >
                  <MapPin size={13} strokeWidth={1.6} /> View Store on Google Maps
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-line rounded-2xl p-5 sm:p-8">
          {/* Invoice summary — captured on the previous page, shown read-only for reassurance */}
          <div
            data-testid="invoice-summary"
            className="mb-6 flex items-center justify-between rounded-2xl border border-line bg-ivory/50 px-4 py-3"
          >
            <div>
              <p className="overline text-[10px]">Invoice Number</p>
              <p className="text-sm font-medium text-ink mt-0.5">{invoice}</p>
            </div>
            <button
              type="button"
              data-testid="change-invoice-btn"
              onClick={() => navigate("/invoice")}
              className="text-[11px] text-maroon hover:underline"
            >
              Change
            </button>
          </div>

          <Field label="Customer Name" required>
            <input
              data-testid="pickup-name-input"
              className="luxury-input"
              placeholder="As mentioned on the invoice"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => touch("name")}
            />
            {err("name") && <p className={luxuryError}>{err("name")}</p>}
          </Field>

          <Field label="Mobile Number" required>
            <div className="flex gap-2">
              <span className="luxury-input !w-16 !text-center text-muted2">+91</span>
              <input
                data-testid="pickup-mobile-input"
                className="luxury-input flex-1"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onBlur={() => touch("mobile")}
              />
            </div>
            {err("mobile") && <p className={luxuryError}>{err("mobile")}</p>}
            <OtpVerificationPanel
              otp={otp}
              mobileValid={mobileValid}
              readyForVerification={mobileValid}
              getPayload={getOtpPayload}
              contextKey={otpContextKey}
              contextNotice={otpContextNotice}
              onRequestStarted={() => setOtpContextNotice("")}
            />
          </Field>

          {/* Pickup date */}
          <div className="mt-5">
            <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
              Select Your Pickup Date <span className="text-maroon">*</span>
            </label>
            <div className="bg-maroon/[0.03] border border-maroon/15 rounded-xl p-4 mb-3 text-[12px] text-ink leading-relaxed" data-testid="pickup-guidance">
              <div className="flex gap-2.5">
                <InfoIcon size={14} strokeWidth={1.6} className="text-maroon flex-shrink-0 mt-0.5" />
                <div>
                  <p>
                    Please allow <span className="font-medium">2–3 days</span> after your scheduled stitching date for final finishing of your outfit before arranging your pickup.
                  </p>
                  <p className="text-muted2 mt-1.5">
                    Example: If your scheduled date is <span className="text-ink">18 August</span>, you can come for pickup on <span className="text-ink">21 August</span>, subject to availability.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-line bg-white p-2 sm:p-3 flex justify-center overflow-hidden" data-testid="pickup-calendar-wrap">
              <Calendar
                mode="single"
                selected={pickupDate ? parseISO(pickupDate) : undefined}
                onSelect={(d) => {
                  if (!d) { setPickupDate(""); return; }
                  setPickupDate(format(d, "yyyy-MM-dd"));
                  touch("pickupDate");
                }}
                disabled={(date) => {
                  const day = date.getDay(); // Sun=0, Sat=6
                  if (day === 0 || day === 6) return true;
                  if (isBefore(date, today) && !isSameDay(date, today)) return true;
                  if (date > addDays(today, 90)) return true;
                  return false;
                }}
                fromDate={today}
                toDate={addDays(today, 90)}
                showOutsideDays={false}
                className="!p-0 w-full"
                classNames={{
                  months: "flex flex-col w-full",
                  month: "w-full space-y-3",
                  caption: "flex justify-center pt-1 relative items-center px-2",
                  caption_label: "font-serif text-base text-ink",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-8 w-8 bg-transparent p-0 rounded-full inline-flex items-center justify-center border border-line text-muted2 hover:text-maroon hover:border-maroon/40 transition-colors",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse",
                  head_row: "grid grid-cols-7",
                  head_cell: "text-muted2 uppercase tracking-[0.14em] text-[10px] py-2 text-center",
                  row: "grid grid-cols-7 mt-1",
                  cell: "relative p-0 text-center text-sm aspect-square flex items-center justify-center",
                  day: "h-9 w-9 sm:h-10 sm:w-10 p-0 font-normal rounded-full text-ink hover:bg-maroon/[0.08] hover:text-maroon transition-colors inline-flex items-center justify-center text-sm",
                  day_selected: "!bg-maroon !text-ivory hover:!bg-maroon hover:!text-ivory shadow-[0_4px_14px_-4px_rgba(106,31,43,0.5)]",
                  day_today: "ring-1 ring-maroon/40 text-maroon",
                  day_disabled: "text-muted2/40 line-through cursor-not-allowed hover:bg-transparent hover:text-muted2/40",
                  day_outside: "text-muted2/40",
                  day_hidden: "invisible",
                }}
              />
            </div>
            {pickupDate && (
              <p className="mt-3 text-[12px] text-ink" data-testid="pickup-date-selected">
                Selected: <span className="font-medium">{format(parseISO(pickupDate), "EEEE, d MMM yyyy")}</span>
              </p>
            )}
            {err("pickupDate") && <p className={luxuryError}>{err("pickupDate")}</p>}
          </div>

          {/* Pickup time slot */}
          <div className="mt-5">
            <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
              Select Your Preferred Pickup Time <span className="text-maroon">*</span>
            </label>
            <p className="text-[11px] text-muted2 mb-3 inline-flex items-center gap-1.5">
              <Clock size={11} strokeWidth={1.7} /> Pickup appointments are available from 12:00 PM to 8:00 PM, Monday–Friday.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {config.slots.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`slot-${s.value}`}
                  onClick={() => { setSlot(s.value); touch("slot"); }}
                  className={`text-xs px-3 py-2.5 rounded-xl border transition-all ${
                    slot === s.value
                      ? "border-maroon bg-maroon/[0.06] text-maroon"
                      : "border-line text-ink hover:border-maroon/40 hover:text-maroon"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {err("slot") && <p className={luxuryError}>{err("slot")}</p>}
          </div>

          {/* Additional Paid Customisations */}
          <Field label="Additional Paid Customisations" required>
            <p className="text-sm text-ink leading-relaxed mb-2">
              Do you have any additional paid customisations to be included with your pickup?
            </p>
            <p className="text-[11px] text-muted2 leading-relaxed mb-3">
              This does not include your regular lehenga or blouse stitching. Please select “Yes” only if you requested and paid for additional customisations beyond our standard stitching service.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { v: "no", t: "No, I don’t have any additional paid customisations." },
                { v: "yes", t: "Yes, please include my additional paid customisations with my pickup." },
              ].map(({ v, t }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setCustomisation(v);
                    if (v === "no") setCustomisationDetails("");
                    touch("customisation");
                  }}
                  data-testid={`customisation-${v}`}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    customisation === v
                      ? "border-maroon bg-maroon/[0.04] shadow-[0_4px_20px_-8px_rgba(106,31,43,0.35)]"
                      : "border-line hover:border-maroon/40"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        customisation === v ? "border-maroon" : "border-line"
                      }`}
                    >
                      {customisation === v && <span className="h-2 w-2 rounded-full bg-maroon" />}
                    </span>
                    <span className="text-sm text-ink leading-relaxed">{t}</span>
                  </div>
                </button>
              ))}
            </div>
            <AnimatePresence initial={false}>
              {customisation === "yes" && (
                <motion.div
                  key="customisation-details"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-5">
                    <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
                      Please describe your additional paid customisation(s).
                    </label>
                    <textarea
                      data-testid="customisation-details-input"
                      rows={6}
                      className="luxury-input luxury-textarea !min-h-[180px] sm:!min-h-[160px]"
                      placeholder="Example: Customised dupatta, additional embroidery, matching potli bag, extra sleeves, or any other paid customisation requested beyond the standard stitching service."
                      value={customisationDetails}
                      onChange={(e) => setCustomisationDetails(e.target.value)}
                      onBlur={() => touch("customisation")}
                    />
                    <p className="text-[11px] text-muted2 italic mt-2 leading-relaxed">
                      Our team will verify these customisations against your original order before your pickup.
                    </p>
                    {err("customisation") && customisation === "yes" && (
                      <p data-testid="customisation-details-error" className={luxuryError}>{err("customisation")}</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {err("customisation") && customisation !== "yes" && (
              <p data-testid="customisation-error" className={luxuryError}>{err("customisation")}</p>
            )}
          </Field>

          <Field label="Special Pickup Instructions">
            <textarea
              data-testid="pickup-instructions-input"
              className="luxury-input luxury-textarea"
              placeholder="Any information you'd like our team to know before your visit."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </Field>

          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="pickup-terms-checkbox"
              checked={terms}
              onChange={(e) => { setTerms(e.target.checked); touch("terms"); }}
              className="mt-1 h-4 w-4 accent-maroon"
            />
            <span className="text-xs text-muted2 leading-relaxed">
              I confirm that all information provided above is accurate and will be used by
              Tanish Textiles only for preparing my in-store pickup.
            </span>
          </label>
          {err("terms") && <p className={`${luxuryError} ml-7`}>{err("terms")}</p>}

          <button
            data-testid="submit-pickup-btn"
            onClick={submit}
            disabled={!canSubmit || otp.loading || (otp.required && !otp.canProceed)}
            className="btn-maroon w-full mt-8 text-base disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Scheduling…"
              : otp.required && !otp.canProceed
              ? "Verify mobile to continue"
              : "Schedule Pickup"}
          </button>
        </div>
      </main>


    </div>
  );
}

const Field = ({ label, required, hint, children }) => (
  <div className="mt-5 first:mt-0">
    <label className="block text-[11px] uppercase tracking-[0.24em] text-ink mb-2">
      {label}{required && <span className="text-maroon"> *</span>}
    </label>
    {children}
    {hint && <p className="text-[11px] text-muted2 mt-1.5">{hint}</p>}
  </div>
);
